// frontend/src/components/crm/AccountDetailView.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { getButtonClasses, getStatusClasses } from '../../utils/brandingUtils';
import crmApi, { activityAPI, opportunityAPI, accountAPI, clientAPI, agencyAPI } from '../../utils/crmApi';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom CSS for scrollbar hiding
const scrollbarHideCSS = `
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
`;

// Inject CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = scrollbarHideCSS;
  document.head.appendChild(style);
}

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

import { 
    BuildingOfficeIcon,
    ArrowLeftIcon,
    PencilIcon,
    TrashIcon,
    UserGroupIcon,
    ChartBarIcon,
    EnvelopeIcon,
    PhoneIcon,
    GlobeAltIcon,
    MapPinIcon,
    CalendarIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    LightBulbIcon,
    ArrowRightIcon,
    PlusIcon,
    EyeIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    StarIcon,
    HeartIcon,
    ChatBubbleLeftRightIcon,
    ClipboardDocumentListIcon,
    PresentationChartLineIcon,
    TrendingUpIcon,
    TrendingDownIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    FireIcon,
    BoltIcon,
    ShieldCheckIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    AdjustmentsHorizontalIcon,
    EllipsisVerticalIcon,
    ShareIcon,
    PrinterIcon,
    ArrowDownTrayIcon,
    TagIcon,
    FlagIcon,
    BookmarkIcon,
    ArchiveBoxIcon,
    ArchiveBoxXMarkIcon,
    DocumentIcon,
    DocumentCheckIcon,
    DocumentDuplicateIcon,
    ArrowPathIcon,
    PresentationChartBarIcon,
    AcademicCapIcon,
    LifebuoyIcon,
    ClipboardDocumentCheckIcon,
    EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import { ComboboxSearch } from '../../components/kibo-ui/combobox/';
import { useDialog } from '../../components/kibo-ui/dialog';

const AccountDetailView = ({ accountId, onBack, onEdit, onDelete }) => {
    const { currentAgency, user } = useCRM();
    const { branding } = useBranding();
    const navigate = useNavigate();
    const location = useLocation();
    const { openDialog } = useDialog();
    
    // Get tab parameter from URL
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const [account, setAccount] = useState(null);
    const [clients, setClients] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(tabParam || 'overview');
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    
    // Enhanced state for new features
    const [opportunities, setOpportunities] = useState([]);
    const [activities, setActivities] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [notes, setNotes] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [revenue, setRevenue] = useState(0);
    const [lastContactDate, setLastContactDate] = useState(null);
    const [nextFollowUp, setNextFollowUp] = useState(null);
    const [accountScore, setAccountScore] = useState(0);
    const [isFavorited, setIsFavorited] = useState(false);
    const [isArchived, setIsArchived] = useState(false);
    const [showStageModal, setShowStageModal] = useState(false);
    const [showMoreDropdown, setShowMoreDropdown] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showRevenueModal, setShowRevenueModal] = useState(false);
    const [showOpportunityModal, setShowOpportunityModal] = useState(false);
    const [activityType, setActivityType] = useState('call');
    const [activityForm, setActivityForm] = useState({
        type: 'call',
        subject: '',
        description: '',
        scheduledDate: '',
        duration: 30,
        outcome: '',
        nextSteps: ''
    });
    
    // Opportunity form state
    const [opportunityForm, setOpportunityForm] = useState({
        name: '',
        value: '',
        stage: 'prospecting',
        probability: 10,
        closeDate: '',
        description: '',
        linkedClientId: '',
        type: 'relocation'
    });
    const [editingOpportunity, setEditingOpportunity] = useState(null);
    
    // Email sending state
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [emailForm, setEmailForm] = useState({
        templateId: '',
        recipientEmail: '',
        recipientName: '',
        variables: {}
    });
    const [sendingEmail, setSendingEmail] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        size: '',
        industry: '',
        logoUrl: '',
        description: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            region: '',
            postalCode: '',
            country: ''
        },
        website: '',
        linkedinUrl: '',
        twitterUrl: '',
        facebookUrl: '',
        primaryContact: {
            name: '',
            email: '',
            phoneE164: '',
            title: '',
            department: ''
        },
        secondaryContacts: [],
        lifecycle: 'lead',
        owner: '',
        tags: [],
        notes: '',
        annualRevenue: 0,
        employeeCount: 0,
        foundedYear: '',
        parentCompany: '',
        subsidiaries: [],
        competitors: [],
        technology: [],
        socialMedia: {},
        customFields: {}
    });
    const [clientFormData, setClientFormData] = useState({
        name: '',
        email: '',
        phoneE164: '',
        countryNationality: '',
        countryResidence: '',
        preferredLanguage: '',
        timezone: '',
        status: 'prospect',
        leadSource: '',
        clientPartnerUserId: '',
        tags: [],
        notes: ''
    });

    const stageConfig = {
        'lead': {
            color: 'yellow',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            textColor: 'text-yellow-800',
            icon: ClockIcon,
            nextStage: 'qualified',
            probability: 10,
            estimatedValue: 0,
            recommendations: [
                'Research the company thoroughly to understand their business model',
                'Identify key decision makers and stakeholders',
                'Reach out with personalized communication highlighting relevant benefits',
                'Schedule an initial discovery call to understand their needs',
                'Qualify the lead based on budget, authority, need, and timeline'
            ]
        },
        'qualified': {
            color: 'blue',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            textColor: 'text-blue-800',
            icon: EyeIcon,
            nextStage: 'proposal',
            probability: 25,
            estimatedValue: 0,
            recommendations: [
                'Conduct detailed needs assessment',
                'Identify pain points and business drivers',
                'Map decision-making process and stakeholders',
                'Develop initial solution outline',
                'Schedule demo or presentation'
            ]
        },
        'proposal': {
            color: 'indigo',
            bgColor: 'bg-indigo-50',
            borderColor: 'border-indigo-200',
            textColor: 'text-indigo-800',
            icon: DocumentTextIcon,
            nextStage: 'negotiation',
            probability: 50,
            estimatedValue: 0,
            recommendations: [
                'Present tailored proposal addressing specific needs',
                'Provide detailed pricing and implementation timeline',
                'Address technical requirements and specifications',
                'Schedule follow-up meeting to discuss proposal',
                'Prepare for objections and questions'
            ]
        },
        'negotiation': {
            color: 'purple',
            bgColor: 'bg-purple-50',
            borderColor: 'border-purple-200',
            textColor: 'text-purple-800',
            icon: ChatBubbleLeftRightIcon,
            nextStage: 'closed_won',
            probability: 75,
            estimatedValue: 0,
            recommendations: [
                'Negotiate terms and pricing',
                'Address final concerns and objections',
                'Prepare contract and legal documents',
                'Coordinate with legal and finance teams',
                'Set clear next steps and timeline'
            ]
        },
        'closed_won': {
            color: 'green',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            textColor: 'text-green-800',
            icon: CheckCircleIcon,
            nextStage: 'customer',
            probability: 100,
            estimatedValue: 0,
            recommendations: [
                'Celebrate the win with your team',
                'Begin onboarding and implementation process',
                'Set up success metrics and KPIs',
                'Schedule regular check-ins',
                'Identify upsell opportunities'
            ]
        },
        'customer': {
            color: 'emerald',
            bgColor: 'bg-emerald-50',
            borderColor: 'border-emerald-200',
            textColor: 'text-emerald-800',
            icon: StarIcon,
            nextStage: null,
            probability: 100,
            estimatedValue: 0,
            recommendations: [
                'Ensure smooth onboarding and implementation',
                'Schedule regular success reviews',
                'Identify opportunities for expansion',
                'Gather testimonials and case studies',
                'Maintain strong relationship for retention'
            ]
        },
        'closed_lost': {
            color: 'red',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            textColor: 'text-red-800',
            icon: XMarkIcon,
            nextStage: 'lead',
            probability: 0,
            estimatedValue: 0,
            recommendations: [
                'Conduct loss analysis and debrief',
                'Document reasons for loss',
                'Update competitive intelligence',
                'Implement lessons learned',
                'Consider re-engagement strategy'
            ]
        },
        'churned': {
            color: 'gray',
            bgColor: 'bg-muted',
            borderColor: 'border-border',
            textColor: 'text-foreground',
            icon: ArchiveBoxIcon,
            nextStage: 'lead',
            probability: 0,
            estimatedValue: 0,
            recommendations: [
                'Conduct exit interview to understand churn reasons',
                'Analyze customer journey and pain points',
                'Implement win-back campaign',
                'Update retention strategies',
                'Learn from experience to prevent future churn'
            ]
        }
    };

    const stages = ['lead', 'qualified', 'proposal', 'negotiation', 'closed_won', 'customer', 'closed_lost', 'churned'];

    // Utility function to test if an image URL is accessible
    const testImageUrl = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        });
    };

    useEffect(() => {
        if (accountId) {
            fetchAccountDetails();
            fetchAdminUsers();
            fetchEmailTemplates();
            fetchActivities();
            refreshClients(); // Ensure clients are loaded
            fetchOpportunities(); // Load opportunities
        }
    }, [accountId]);

    // Monitor clients state changes
    useEffect(() => {
    }, [clients]);

    const fetchActivities = async () => {
        try {
            const response = await activityAPI.getForEntity('account', accountId, { limit: 50 });
            if (response.data.success) {
                setActivities(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
        }
    };

    const fetchOpportunities = async () => {
        try {
            const response = await opportunityAPI.getByAccount(accountId);
            setOpportunities(response.data);
        } catch (error) {
            console.error('Error fetching opportunities:', error);
            toast.error('Failed to load opportunities');
        }
    };

    const fetchAccountDetails = async () => {
        try {
            setLoading(true);
            
            const [accountRes, clientsRes] = await Promise.all([
                accountAPI.getById(accountId),
                clientAPI.getAll({ accountId, limit: 50 })
            ]);

            const accountData = accountRes.data;
            if (accountData.success) {
                setAccount(accountData.data);
            } else if (accountRes.response?.status === 404) {
                setError('Account not found');
            } else {
                setError('Failed to load account details');
            }
            
            const clientsData = clientsRes.data;
            if (clientsData.success) {
                setClients(clientsData.data || []);
            } else {
                setClients([]);
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError('Account not found');
            } else {
                setError('Failed to fetch account details');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchAdminUsers = async () => {
        try {
            if (!currentAgency?._id) {
                return;
            }

            const response = await agencyAPI.getMembers(currentAgency._id);
            const data = response.data;
            if (data.success) {
                setAdminUsers(data.members || []);
            } else {
                console.error('Failed to fetch admin users:', data.message);
            }
        } catch (error) {
            console.error('Error fetching admin users:', error);
        }
    };

    const handleStageChange = async (newStage) => {
        try {
            await accountAPI.update(accountId, { lifecycle: newStage });
            setAccount(prev => ({ ...prev, lifecycle: newStage }));
            toast.success('Account stage updated successfully');
        } catch (error) {
            console.error('Error updating account stage:', error);
            toast.error('Failed to update account stage');
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const response = await crmApi.get('/email-templates');
            const data = response.data;
            if (data.success) {
                setEmailTemplates(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching email templates:', error);
        }
    };

    const handleOpenEmailModal = () => {
        setEmailForm({
            templateId: '',
            recipientEmail: account?.primaryContact?.email || '',
            recipientName: account?.primaryContact?.name || account?.name || '',
            variables: {}
        });
        setShowEmailModal(true);
    };

    const handleEmailFormChange = (field, value) => {
        if (field === 'templateId') {
            const template = emailTemplates.find(t => t._id === value);
            setSelectedTemplate(template);
            
            // Auto-populate variables with account data
            const autoVariables = {
                'client.name': account?.primaryContact ? (`${account.primaryContact.firstName || ''} ${account.primaryContact.lastName || ''}`.trim() || account.primaryContact.name || 'Client Name') : 'Client Name',
                'client.email': account?.primaryContact?.email || 'client@example.com',
                'client.company': account?.name || 'N/A',
                'account.name': account?.name || 'N/A',
                'account.contact': account?.primaryContact ? (`${account.primaryContact.firstName || ''} ${account.primaryContact.lastName || ''}`.trim() || account.primaryContact.name || 'N/A') : 'N/A',
                'partner.name': account?.ownerUserId?.name || 'N/A',
                'current.date': new Date().toLocaleDateString(),
                'current.time': new Date().toLocaleTimeString()
            };
            
            setEmailForm(prev => ({
                ...prev,
                [field]: value,
                variables: autoVariables
            }));
        } else {
            setEmailForm(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const replaceVariables = (content, variables) => {
        if (!content || !variables) return content;
        
        let processedContent = content;
        Object.keys(variables).forEach(key => {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            processedContent = processedContent.replace(regex, variables[key] || `{{${key}}}`);
        });
        
        return processedContent;
    };

    // Additional button handlers
    const handleShare = async () => {
        try {
            const shareData = {
                title: `${account?.name} - Account Details`,
                text: `View account details for ${account?.name}`,
                url: window.location.href
            };
            
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback: copy to clipboard
                await navigator.clipboard.writeText(window.location.href);
                toast.success('Account link copied to clipboard');
            }
        } catch (error) {
            console.error('Error sharing:', error);
            toast.error('Failed to share account');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExport = async () => {
        try {
            const exportData = {
                account: account,
                clients: clients,
                activities: activities,
                opportunities: opportunities,
                documents: documents,
                exportedAt: new Date().toISOString(),
                exportedBy: currentAgency?.name || 'Unknown'
            };
            
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = `${account?.name || 'account'}_export_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            toast.success('Account data exported successfully');
        } catch (error) {
            console.error('Error exporting:', error);
            toast.error('Failed to export account data');
        }
    };

    const handleArchive = async () => {
        try {
            await accountAPI.update(accountId, { isArchived: !isArchived });
            setIsArchived(!isArchived);
            toast.success(`Account ${!isArchived ? 'archived' : 'unarchived'} successfully`);
        } catch (error) {
            console.error('Error archiving account:', error);
            toast.error('Failed to archive account');
        }
    };

    const handleFavorite = async () => {
        try {
            await accountAPI.update(accountId, { isFavorited: !isFavorited });
            setIsFavorited(!isFavorited);
            toast.success(`Account ${!isFavorited ? 'added to' : 'removed from'} favorites`);
        } catch (error) {
            console.error('Error updating favorite status:', error);
            toast.error('Failed to update favorite status');
        }
    };

    const handleSendEmail = async () => {
        try {
            setSendingEmail(true);
            const emailData = {
                templateId: emailForm.templateId,
                accountId: accountId,
                recipientEmail: emailForm.recipientEmail,
                recipientName: emailForm.recipientName,
                variables: emailForm.variables
            };

            const response = await crmApi.post(`/clients-enhanced/${accountId}/send-email`, emailData);
            const data = response.data;
            if (data.success) {
                toast.success('Email sent successfully!');
                setShowEmailModal(false);
                setEmailForm({
                    templateId: '',
                    recipientEmail: '',
                    recipientName: '',
                    variables: {}
                });
                setSelectedTemplate(null);
            } else {
                throw new Error('Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send email: ' + error.message);
        } finally {
            setSendingEmail(false);
        }
    };

    const handleAddClient = () => {
        setShowAddClientModal(true);
    };

    const refreshClients = async () => {
        try {
            const response = await clientAPI.getAll({ accountId, limit: 50 });
            const data = response.data;
            if (data.success) {
                setClients(data.data || []);
            }
        } catch (error) {
            // Silently fail - clients will refresh on next load
        }
    };

    const handleViewClient = (clientId) => {
        // Navigate to client detail view with account context
        navigate(`/clients/${clientId}?fromAccount=${accountId}`);
    };

    // Opportunity management functions
    const handleOpportunityFormChange = (e) => {
        const { name, value } = e.target;
        setOpportunityForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddOpportunity = async (e) => {
        e.preventDefault();
        try {
            const opportunityData = {
                ...opportunityForm,
                accountId: accountId,
                value: parseFloat(opportunityForm.value) || 0,
                closeDate: opportunityForm.closeDate || new Date().toISOString(),
                linkedClientId: opportunityForm.linkedClientId || null
            };
            
            const newOpportunity = await opportunityAPI.create(opportunityData);
            setOpportunities(prev => [...prev, newOpportunity.data]);
            setShowOpportunityModal(false);
            resetOpportunityForm();
            toast.success('Opportunity added successfully');
        } catch (error) {
            console.error('Error adding opportunity:', error);
            toast.error('Failed to add opportunity: ' + error.message);
        }
    };

    const handleEditOpportunity = (opportunity) => {
        setEditingOpportunity(opportunity);
        setOpportunityForm({
            name: opportunity.name,
            value: opportunity.value.toString(),
            stage: opportunity.stage,
            probability: opportunity.probability,
            closeDate: opportunity.closeDate ? new Date(opportunity.closeDate).toISOString().split('T')[0] : '',
            description: opportunity.description || '',
            linkedClientId: opportunity.linkedClient?._id || '',
            type: opportunity.type || 'relocation'
        });
        setShowOpportunityModal(true);
    };

    const handleUpdateOpportunity = async (e) => {
        e.preventDefault();
        try {
            const updateData = {
                ...opportunityForm,
                value: parseFloat(opportunityForm.value) || 0,
                closeDate: opportunityForm.closeDate || new Date().toISOString(),
                linkedClientId: opportunityForm.linkedClientId || null
            };
            
            const updatedOpportunity = await opportunityAPI.update(editingOpportunity._id, updateData);
            setOpportunities(prev => prev.map(opp => 
                opp._id === editingOpportunity._id ? updatedOpportunity.data : opp
            ));
            setShowOpportunityModal(false);
            resetOpportunityForm();
            toast.success('Opportunity updated successfully');
        } catch (error) {
            console.error('Error updating opportunity:', error);
            toast.error('Failed to update opportunity: ' + error.message);
        }
    };

    const resetOpportunityForm = () => {
        setOpportunityForm({
            name: '',
            value: '',
            stage: 'prospecting',
            probability: 10,
            closeDate: '',
            description: '',
            linkedClientId: '',
            type: 'relocation'
        });
        setEditingOpportunity(null);
    };

    const handleClientFormChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setClientFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setClientFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleClientSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await clientAPI.create({
                ...clientFormData,
                accountId: accountId
            });
            const data = response.data;
            if (data.success) {
                setShowAddClientModal(false);
                setClientFormData({
                    name: '',
                    email: '',
                    phoneE164: '',
                    countryNationality: '',
                    countryResidence: '',
                    preferredLanguage: '',
                    timezone: '',
                    status: 'prospect',
                    leadSource: '',
                    clientPartnerUserId: '',
                    tags: [],
                    notes: ''
                });
                fetchAccountDetails(); // Refresh the data
                toast.success('Client added successfully');
            }
        } catch (error) {
            console.error('Error creating client:', error);
            toast.error('Failed to create client');
        }
    };

    const handleEdit = () => {
        if (account) {
            setFormData({
                name: account.name,
                size: account.size,
                address: account.address,
                website: account.website || '',
                logoUrl: account.logoUrl || '',
                primaryContact: account.primaryContact,
                lifecycle: account.lifecycle,
                owner: account.ownerUserId?._id || account.ownerUserId || '',
                tags: account.tags || [],
                notes: account.notes || ''
            });
            setShowEditModal(true);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await accountAPI.update(accountId, formData);
            const data = response.data;
            if (data.success) {
                setAccount(data.data);
                setShowEditModal(false);
                toast.success('Account updated successfully');
                if (onEdit) {
                    onEdit(data.data);
                }
            }
        } catch (error) {
            console.error('Error updating account:', error);
            toast.error('Failed to update account');
        }
    };

    const getStageProgress = () => {
        const currentIndex = stages.indexOf(account?.lifecycle);
        return {
            current: currentIndex,
            total: stages.length,
            percentage: ((currentIndex + 1) / stages.length) * 100
        };
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
            </div>
        );
    }

    if (error || !account) {
        return (
            <div className="border border-red-200 text-red-700 px-4 py-3 rounded" style={{ backgroundColor: `${branding.accentColor}20` }}>
                {error || 'Account not found'}
            </div>
        );
    }

    const currentStageConfig = stageConfig[account.lifecycle];
    const progress = getStageProgress();

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-sm border border-border">
                <div className="px-6 py-4 border-b border-border">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={onBack}
                                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                            >
                                <ArrowLeftIcon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-4">
                                {/* Company Logo */}
                                <div className="relative">
                                    {account.logoUrl && account.logoUrl.trim() !== '' ? (
                                        <img 
                                            src={account.logoUrl} 
                                            alt={`${account.name} logo`}
                                            className="w-16 h-16 rounded-lg object-contain bg-card border-2 border-border shadow-sm"
                                            crossOrigin="anonymous"
                                            onError={(e) => {
                                                console.warn('Logo failed to load, showing fallback:', account.logoUrl);
                                                e.target.style.display = 'none';
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback) {
                                                    fallback.style.display = 'flex';
                                                }
                                            }}
                                            onLoad={() => console.log('Logo loaded successfully:', account.logoUrl)}
                                        />
                                    ) : null}
                                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${account.logoUrl && account.logoUrl.trim() !== '' ? 'hidden' : 'flex'}`}>
                                        <div className="rounded-lg flex items-center justify-center text-white font-bold h-16 w-16 text-lg bg-gradient-to-br from-purple-500 to-blue-600">
                                            {account.name ? account.name.charAt(0).toUpperCase() : 'C'}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Company Info */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-2xl font-bold text-foreground">{account.name}</h1>
                                        {/* Account Score */}
                                        <div className="flex items-center gap-1 px-2 py-1 bg-[var(--brand-primary)]/10 rounded-full">
                                            <StarIcon className="h-4 w-4 text-[var(--brand-primary)]" />
                                            <span className="text-sm font-medium text-[var(--brand-primary)]">{accountScore}</span>
                                        </div>
                                        {/* Favorite Button */}
                                        <button
                                            onClick={handleFavorite}
                                            className={`p-1 rounded-full transition-colors ${
                                                isFavorited ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                                            }`}
                                            style={{ color: isFavorited ? branding.accentColor : undefined }}
                                        >
                                            <HeartIcon className={`h-5 w-5 ${isFavorited ? 'fill-current' : ''}`} />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        <span>{account.size || account.employeeCount || 0} employees</span>
                                        {account.industry && <span>• {account.industry}</span>}
                                        {account.annualRevenue > 0 && <span>• ${account.annualRevenue.toLocaleString()} ARR</span>}
                                        {account.foundedYear && <span>• Est. {account.foundedYear}</span>}
                                    </div>
                                    {/* Tags */}
                                    {account.tags && account.tags.length > 0 && (
                                        <div className="flex items-center gap-2 mt-2">
                                            {account.tags.slice(0, 3).map((tag, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                            {account.tags.length > 3 && (
                                                <span className="text-xs text-muted-foreground">+{account.tags.length - 3} more</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Quick Actions */}
                            <button
                                onClick={() => setShowActivityModal(true)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('primary', branding)}`}
                            >
                                <PlusIcon className="h-4 w-4" />
                                Log Activity
                            </button>
                            
                            {hasPermission(user, PERMISSIONS.SEND_EMAIL, currentAgency?._id) && (
                            <button
                                onClick={handleOpenEmailModal}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('info', branding)}`}
                            >
                                <EnvelopeIcon className="h-4 w-4" />
                                Send Email
                            </button>
                            )}
                            
                            <button
                                onClick={() => setShowRevenueModal(true)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('success', branding)}`}
                            >
                                <CurrencyDollarIcon className="h-4 w-4" />
                                Add Revenue
                            </button>
                            
                            {/* More Actions Dropdown */}
                            <div className="relative group">
                                <button 
                                    onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('outline', branding)}`}
                                >
                                    <EllipsisVerticalIcon className="h-4 w-4" />
                                    More
                                </button>
                                
                                <div className={`absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg transition-all duration-200 z-50 ${
                                    showMoreDropdown ? 'opacity-100 visible' : 'opacity-0 invisible'
                                }`}>
                                    <div className="py-1">
                                        {hasPermission(user, PERMISSIONS.EDIT_ACCOUNT, currentAgency?._id) && (
                                            <button
                                                onClick={() => {
                                                    handleEdit();
                                                    setShowMoreDropdown(false);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted flex items-center gap-2"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                                Edit Account
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                handleShare();
                                                setShowMoreDropdown(false);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted flex items-center gap-2"
                                        >
                                            <ShareIcon className="h-4 w-4" />
                                            Share
                                        </button>
                                        <button
                                            onClick={() => {
                                                handlePrint();
                                                setShowMoreDropdown(false);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted flex items-center gap-2"
                                        >
                                            <PrinterIcon className="h-4 w-4" />
                                            Print
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleExport();
                                                setShowMoreDropdown(false);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted flex items-center gap-2"
                                        >
                                            <ArrowDownTrayIcon className="h-4 w-4" />
                                            Export
                                        </button>
                                        <hr className="my-1" />
                                        <button
                                            onClick={() => {
                                                handleArchive();
                                                setShowMoreDropdown(false);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted flex items-center gap-2"
                                        >
                                            {isArchived ? <ArchiveBoxXMarkIcon className="h-4 w-4" /> : <ArchiveBoxIcon className="h-4 w-4" />}
                                            {isArchived ? 'Unarchive' : 'Archive'}
                                        </button>
                                        {hasPermission(user, PERMISSIONS.DELETE_ACCOUNT, currentAgency?._id) && (
                                            <button
                                                onClick={() => {
                                                    onDelete(account._id);
                                                    setShowMoreDropdown(false);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2"
                                                style={{ 
                                                    color: branding.accentColor,
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = `${branding.accentColor}20`}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Stage Timeline */}
                <div className="px-6 py-4">
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-foreground">Sales Pipeline Progress</h3>
                            <button
                                onClick={() => setShowStageModal(true)}
                                className={`flex items-center gap-2 px-3 py-1 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('outline', branding)}`}
                            >
                                <ArrowRightIcon className="h-4 w-4" />
                                Move Stage
                            </button>
                        </div>
                        
                        {/* Progress Overview */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-card p-4 rounded-lg border border-border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-foreground">Pipeline Progress</span>
                                    <span className="text-sm font-bold text-[var(--brand-primary)]">{Math.round(progress.percentage)}%</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div 
                                        className="bg-[var(--brand-primary)] h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${progress.percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                            
                            <div className="bg-card p-4 rounded-lg border border-border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-foreground">Win Probability</span>
                                    <span className="text-sm font-bold text-[var(--brand-secondary)]">{currentStageConfig.probability}%</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div 
                                        className="bg-[var(--brand-secondary)] h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${currentStageConfig.probability}%` }}
                                    ></div>
                                </div>
                            </div>
                            
                            <div className="bg-card p-4 rounded-lg border border-border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-foreground">Estimated Value</span>
                                    <span className="text-sm font-bold text-[var(--brand-accent)]">${currentStageConfig.estimatedValue.toLocaleString()}</span>
                                </div>
                                <div className="text-xs text-muted-foreground">Based on stage probability</div>
                            </div>
                        </div>
                    </div>

                    {/* Modern Pipeline Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {stages.map((stage, index) => {
                            const stageInfo = stageConfig[stage];
                            const isActive = stage === account.lifecycle;
                            const currentIndex = stages.indexOf(account.lifecycle);
                            const isCompleted = index < currentIndex;
                            const isUpcoming = index > currentIndex;
                            
                            return (
                                <div key={stage} className="relative">
                                    {/* Connection Arrow */}
                                    {index < stages.length - 1 && (
                                        <div className="hidden md:block absolute top-6 -right-2 z-10">
                                            <div className={`w-4 h-4 transform rotate-45 ${
                                                isCompleted ? 'text-white' : 'bg-gray-300'
                                            }`}></div>
                                        </div>
                                    )}
                                    
                                    {/* Stage Card */}
                                    <div className={`relative p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${
                                        isActive 
                                            ? `${stageInfo.bgColor} ${stageInfo.borderColor} shadow-lg ring-2 ring-blue-200`
                                            :                                                 isCompleted 
                                                ? 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 shadow-md'
                                                : 'bg-card border-border shadow-sm'
                                    }`}>
                                        {/* Stage Icon */}
                                        <div className="flex items-center justify-center mb-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                isActive 
                                                    ? `${stageInfo.bgColor} ${stageInfo.textColor}`
                                                    : isCompleted 
                                                        ? 'text-white'
                                                        : 'bg-muted text-muted-foreground'
                                            }`}
                                            style={isCompleted && !isActive ? { backgroundColor: branding.primaryColor } : {}}
                                            >
                                                {isCompleted && !isActive ? (
                                                    <CheckCircleIcon className="h-5 w-5" />
                                                ) : (
                                                    <stageInfo.icon className="h-5 w-5" />
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Stage Info */}
                                        <div className="text-center">
                                            <h4 className={`text-sm font-bold ${
                                                isActive ? stageInfo.textColor : isCompleted ? 'text-[var(--brand-primary)]' : 'text-muted-foreground'
                                            }`}>
                                                {stage.charAt(0).toUpperCase() + stage.slice(1)}
                                            </h4>
                                            
                                            {isActive && (
                                                <div className="mt-2">
                                                    <button
                                                        onClick={() => {
                                                            const nextStage = stageInfo.nextStage;
                                                            if (nextStage) {
                                                                handleStageChange(nextStage);
                                                            }
                                                        }}
                                                        className="text-xs hover:opacity-80 font-medium px-3 py-1 rounded-full transition-colors"
                                                        style={{ color: branding.primaryColor, backgroundColor: `${branding.primaryColor}20` }}
                                                    >
                                                        {stageInfo.nextStage ? `Move to ${stageInfo.nextStage}` : 'Final Stage'}
                                                    </button>
                                                </div>
                                            )}
                                            
                                            {isCompleted && (
                                                <div className="mt-1 text-xs font-medium" style={{ color: branding.primaryColor }}>
                                                    ✓ Completed
                                                </div>
                                            )}
                                            
                                            {isUpcoming && (
                                                <div className="mt-1 text-xs text-muted-foreground">
                                                    Upcoming
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-card rounded-lg shadow-sm border border-border">
                <div className="border-b border-border">
                    <nav className="flex space-x-4 px-6 overflow-x-auto scrollbar-thin">
                        {[
                            { id: 'overview', label: 'Overview', icon: BuildingOfficeIcon },
                            { id: 'clients', label: 'Clients', icon: UserGroupIcon, count: clients.length },
                            { id: 'activities', label: 'Activities', icon: ClipboardDocumentListIcon, count: activities.length },
                            { id: 'opportunities', label: 'Opportunities', icon: CurrencyDollarIcon, count: opportunities.length },
                            { id: 'documents', label: 'Documents', icon: DocumentTextIcon, count: documents.length },
                            { id: 'analytics', label: 'Analytics', icon: ChartBarIcon },
                            { id: 'recommendations', label: 'Recommendations', icon: LightBulbIcon }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    if (tab.id === 'clients') {
                                        refreshClients();
                                    }
                                }}
                                className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                                    activeTab === tab.id
                                        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                                }`}
                            >
                                <tab.icon className="h-5 w-5 flex-shrink-0" />
                                <span className="hidden sm:inline">{tab.label}</span>
                                {tab.count !== undefined && (
                                    <span className="bg-muted text-muted-foreground py-0.5 px-2 rounded-full text-xs flex-shrink-0">
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Company Information */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-foreground">Company Information</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <BuildingOfficeIcon className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <div className="text-sm font-medium text-foreground">{account.name}</div>
                                                <div className="text-sm text-muted-foreground">{account.size} employees</div>
                                            </div>
                                        </div>
                                        {account.website && (
                                            <div className="flex items-center gap-3">
                                            <GlobeAltIcon className="h-5 w-5 text-muted-foreground" />
                                                <a 
                                                    href={account.website} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="text-sm hover:opacity-80 transition-opacity"
                                                    style={{ color: branding.primaryColor }}
                                                >
                                                    {account.website}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <UserGroupIcon className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <div className="text-sm font-medium text-foreground">{account.primaryContact?.name}</div>
                                                <div className="text-sm text-muted-foreground">Primary Contact</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
                                            <a 
                                                href={`mailto:${account.primaryContact?.email}`}
                                                className="text-sm hover:opacity-80 transition-opacity"
                                                style={{ color: branding.primaryColor }}
                                            >
                                                {account.primaryContact?.email}
                                            </a>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                                            <a 
                                                href={`tel:${account.primaryContact?.phoneE164}`}
                                                className="text-sm hover:opacity-80 transition-opacity"
                                                style={{ color: branding.primaryColor }}
                                            >
                                                {account.primaryContact?.phoneE164}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-3">Address</h3>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div className="flex items-start gap-3">
                                        <MapPinIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div className="text-sm text-foreground">
                                            <div>{account.address.line1}</div>
                                            {account.address.line2 && <div>{account.address.line2}</div>}
                                            <div>{account.address.city}, {account.address.region} {account.address.postalCode}</div>
                                            <div className="font-medium">{account.address.country}</div>
                                        </div>
                                    </div>
                                    
                                    {/* Interactive Map */}
                                    <div className="overflow-hidden rounded-lg border border-border max-w-full relative z-0">
                                        <div className="h-64 w-full relative z-0">
                                            <MapContainer
                                                center={[40.4168, -3.7038]} // Madrid coordinates
                                                zoom={8}
                                                style={{ height: '100%', width: '100%', zIndex: 0 }}
                                                scrollWheelZoom={true}
                                            >
                                                <TileLayer
                                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                />
                                                <Marker position={[40.4168, -3.7038]}>
                                                    <Popup>
                                                        <div className="text-center">
                                                            <div className="font-semibold text-popover-foreground">{account.name}</div>
                                                            <div className="text-sm text-muted-foreground">{account.address.city}, {account.address.country}</div>
                                                        </div>
                                                    </Popup>
                                                </Marker>
                                            </MapContainer>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tags and Notes */}
                            {(account.tags?.length > 0 || account.notes) && (
                                <div className="space-y-4">
                                    {account.tags?.length > 0 && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-foreground mb-3">Tags</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {account.tags.map((tag, index) => (
                                                    <span
                                                        key={index}
                                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-blue-800"
                                                        style={{ backgroundColor: `${branding.primaryColor}20` }}
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {account.notes && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-foreground mb-3">Notes</h3>
                                            <div className="text-sm text-foreground bg-muted p-3 rounded-lg">
                                                {account.notes}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'activities' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Recent Activities</h3>
                                <button 
                                    onClick={() => setShowActivityModal(true)}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('primary', branding)}`}
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Log Activity
                                </button>
                            </div>
                            
                            {activities.length > 0 ? (
                                <div className="space-y-3">
                                    {activities.map((activity, index) => {
                                        const statusClasses = getStatusClasses(activity.type, 'status', branding || {});
                                        return (
                                        <div key={index} className="bg-muted rounded-lg p-4">
                                            <div className="flex items-start gap-3">
                                                <div className={`p-2 rounded-full ${statusClasses.bgColor}`}>
                                                    {activity.type === 'call' && <PhoneIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'email' && <EnvelopeIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'meeting' && <CalendarIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'task' && <ClipboardDocumentListIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'note' && <DocumentTextIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'document' && <DocumentIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'proposal' && <DocumentCheckIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'contract' && <DocumentDuplicateIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'payment' && <CurrencyDollarIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'follow_up' && <ArrowPathIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'demo' && <PresentationChartBarIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'presentation' && <PresentationChartLineIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'site_visit' && <MapPinIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'training' && <AcademicCapIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'support' && <LifebuoyIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'complaint' && <ExclamationTriangleIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'feedback' && <ChatBubbleLeftRightIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'survey' && <ClipboardDocumentCheckIcon className="h-4 w-4 text-white" />}
                                                    {activity.type === 'other' && <EllipsisHorizontalIcon className="h-4 w-4 text-white" />}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-medium text-foreground">{activity.subject}</h4>
                                                        <span className="text-sm text-muted-foreground">
                                                            {new Date(activity.createdAt).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-xs font-medium text-muted-foreground capitalize">
                                                            {activity.type.replace('_', ' ')}
                                                        </span>
                                                        {activity.duration && (
                                                            <span className="text-xs text-muted-foreground">
                                                                • {activity.duration} min
                                                            </span>
                                                        )}
                                                        {activity.status && (
                                                            <span className={`text-xs px-2 py-1 rounded-full ${
                                                                activity.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                                activity.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                                                activity.status === 'planned' ? 'bg-yellow-100 text-yellow-700' :
                                                                'bg-muted text-foreground'
                                                            }`}>
                                                                {activity.status.replace('_', ' ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground mt-2">{activity.description}</p>
                                                    {activity.outcome && (
                                                        <div className="mt-2">
                                                            <span className="text-xs font-medium text-foreground">Outcome: </span>
                                                            <span className="text-xs text-muted-foreground capitalize">
                                                                {activity.outcome.replace('_', ' ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {activity.nextSteps && (
                                                        <div className="mt-2">
                                                            <span className="text-xs font-medium text-foreground">Next Steps: </span>
                                                            <span className="text-xs text-muted-foreground">{activity.nextSteps}</span>
                                                        </div>
                                                    )}
                                                    {activity.participants && activity.participants.length > 0 && (
                                                        <div className="mt-2">
                                                            <span className="text-xs font-medium text-foreground">Participants: </span>
                                                            <span className="text-xs text-muted-foreground">
                                                                {activity.participants.map(p => p.name).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <ClipboardDocumentListIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                    <p>No activities logged yet.</p>
                                    <button 
                                        onClick={() => setShowActivityModal(true)}
                                        className={`mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('primary', branding || {})}`}
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        Log First Activity
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'opportunities' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Sales Opportunities</h3>
                                <button 
                                    onClick={() => {
                                        // Navigate to opportunity creation with account data
                                        navigate('/opportunities/new', { 
                                            state: { 
                                                accountId: account._id,
                                                accountName: account.name
                                            }
                                        });
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('primary', branding)}`}
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Add Opportunity
                                </button>
                            </div>
                            
                            {opportunities.length > 0 ? (
                                <div className="grid gap-4">
                                    {opportunities.map((opportunity, index) => (
                                        <div key={index} className="bg-card border border-border rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-medium text-foreground">{opportunity.name}</h4>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-lg font-bold text-[var(--brand-primary)]">${opportunity.value.toLocaleString()}</span>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                // Navigate to edit opportunity
                                                                navigate(`/opportunities/${opportunity._id}/edit`);
                                                            }}
                                                            className="p-1 text-muted-foreground hover:text-blue-600 transition-colors"
                                                            title="Edit opportunity"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                // Delete opportunity using modal
                                                                openDialog({
                                                                    title: 'Delete Opportunity',
                                                                    description: `Are you sure you want to delete "${opportunity.name}"? This action cannot be undone.`,
                                                                    confirmText: 'Delete',
                                                                    cancelText: 'Cancel',
                                                                    variant: 'danger',
                                                                    onConfirm: () => {
                                                                        opportunityAPI.delete(opportunity._id)
                                                                            .then(() => {
                                                                                setOpportunities(prev => prev.filter(opp => opp._id !== opportunity._id));
                                                                                toast.success('Opportunity deleted successfully');
                                                                            })
                                                                            .catch(error => {
                                                                                console.error('Error deleting opportunity:', error);
                                                                                toast.error('Failed to delete opportunity');
                                                                            });
                                                                    }
                                                                });
                                                            }}
                                                            className="p-1 text-muted-foreground hover:text-red-600 transition-colors"
                                                            title="Delete opportunity"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                <span>Stage: {opportunity.stage}</span>
                                                <span>Probability: {opportunity.probability}%</span>
                                                <span>Close Date: {new Date(opportunity.closeDate).toLocaleDateString()}</span>
                                            </div>
                                            {opportunity.linkedClient && (
                                                <div className="mt-2 flex items-center gap-2 text-sm">
                                                    <UserGroupIcon className="h-4 w-4 text-muted-foreground" />
                                                    <span className="text-muted-foreground">Linked to:</span>
                                                    <button
                                                        onClick={() => handleViewClient(opportunity.linkedClient._id)}
                                                        className="text-[var(--brand-primary)] hover:underline font-medium"
                                                    >
                                                        {opportunity.linkedClient.name}
                                                    </button>
                                                </div>
                                            )}
                                            {opportunity.description && (
                                                <p className="text-sm text-muted-foreground mt-2">{opportunity.description}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <CurrencyDollarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                    <p>No opportunities tracked yet.</p>
                                    <button 
                                        onClick={() => {
                                            // Navigate to opportunity creation with account data
                                            navigate('/opportunities/new', { 
                                                state: { 
                                                    accountId: account._id,
                                                    accountName: account.name
                                                }
                                            });
                                        }}
                                        className={`mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('primary', branding)}`}
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        Add First Opportunity
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'documents' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Documents & Files</h3>
                                <button 
                                    onClick={() => {/* Implement file upload */}}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('primary', branding)}`}
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Upload Document
                                </button>
                            </div>
                            
                            {documents.length > 0 ? (
                                <div className="grid gap-3">
                                    {documents.map((doc, index) => (
                                        <div key={index} className="bg-card border border-border rounded-lg p-4">
                                            <div className="flex items-center gap-3">
                                                <DocumentTextIcon className="h-8 w-8 text-muted-foreground" />
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-foreground">{doc.name}</h4>
                                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                        <span>{doc.type}</span>
                                                        <span>{doc.size}</span>
                                                        <span>Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                                <button className="text-[var(--brand-primary)] hover:text-[var(--brand-primary)]/80">
                                                    <ArrowDownTrayIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <DocumentTextIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                    <p>No documents uploaded yet.</p>
                                    <button 
                                        onClick={() => {/* Implement file upload */}}
                                        className={`mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClasses('primary', branding)}`}
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        Upload First Document
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'clients' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Associated Clients</h3>
                                <button 
                                    onClick={handleAddClient}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: branding.primaryColor }}
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Add Client
                                </button>
                            </div>
                            
                            {clients.length > 0 ? (
                                <div className="grid gap-4">
                                    {clients.map(client => (
                                        <div 
                                            key={client._id} 
                                            className="bg-muted rounded-lg p-4 cursor-pointer hover:bg-muted transition-colors"
                                            onClick={() => handleViewClient(client._id)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="font-medium text-foreground">{`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || 'Unknown Client'}</div>
                                                    <div className="text-sm text-muted-foreground">{client.email}</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                        client.status === 'active' ? 'bg-green-100 text-green-800' :
                                                        client.status === 'prospect' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {client.status}
                                                    </span>
                                                    <button 
                                                        className="hover:opacity-80 transition-opacity p-1"
                                                        style={{ color: branding.primaryColor }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewClient(client._id);
                                                        }}
                                                    >
                                                        <EyeIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                    <p>No clients associated with this account yet.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'analytics' && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-foreground">Account Analytics</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="rounded-lg p-4" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                    <div className="flex items-center gap-3">
                                        <UserGroupIcon className="h-8 w-8" style={{ color: branding.primaryColor }} />
                                        <div>
                                            <div className="text-2xl font-bold" style={{ color: branding.primaryColor }}>{clients.length}</div>
                                            <div className="text-sm" style={{ color: branding.primaryColor }}>Linked Clients</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="rounded-lg p-4" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                    <div className="flex items-center gap-3">
                                        <CheckCircleIcon className="h-8 w-8" style={{ color: branding.secondaryColor }} />
                                        <div>
                                            <div className="text-2xl font-bold" style={{ color: branding.secondaryColor }}>{account.stats?.closedTasks || 0}</div>
                                            <div className="text-sm" style={{ color: branding.secondaryColor }}>Completed Tasks</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="rounded-lg p-4" style={{ backgroundColor: `${branding.accentColor}20` }}>
                                    <div className="flex items-center gap-3">
                                        <ChartBarIcon className="h-8 w-8" style={{ color: branding.accentColor }} />
                                        <div>
                                            <div className="text-2xl font-bold" style={{ color: branding.accentColor }}>{account.stats?.openPct?.toFixed(1) || 0}%</div>
                                            <div className="text-sm" style={{ color: branding.accentColor }}>Open Task %</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'recommendations' && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <LightBulbIcon className="h-6 w-6" style={{ color: branding.accentColor }} />
                                <h3 className="text-lg font-semibold text-foreground">
                                    Recommendations for {account.lifecycle} Stage
                                </h3>
                            </div>
                            
                            <div className="rounded-lg p-6 border" style={{ 
                                background: `linear-gradient(to right, ${branding.primaryColor}20, ${branding.secondaryColor}20)`,
                                borderColor: `${branding.primaryColor}40`
                            }}>
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-lg ${currentStageConfig.bgColor}`}>
                                        <currentStageConfig.icon className={`h-6 w-6 ${currentStageConfig.textColor}`} />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-foreground mb-2">
                                            Current Stage: {account.lifecycle.charAt(0).toUpperCase() + account.lifecycle.slice(1)}
                                        </h4>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            {currentStageConfig.nextStage 
                                                ? `Ready to move to ${currentStageConfig.nextStage} stage?`
                                                : 'This is the final stage in the pipeline.'
                                            }
                                        </p>
                                        
                                        {currentStageConfig.nextStage && (
                                            <button
                                                onClick={() => handleStageChange(currentStageConfig.nextStage)}
                                                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                                                style={{ backgroundColor: branding.primaryColor }}
                                            >
                                                <ArrowRightIcon className="h-4 w-4" />
                                                Move to {currentStageConfig.nextStage}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="font-semibold text-foreground">Action Items</h4>
                                <div className="space-y-3">
                                    {currentStageConfig.recommendations.map((recommendation, index) => (
                                        <div key={index} className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border">
                                            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                                <span className="text-xs font-semibold" style={{ color: branding.primaryColor }}>{index + 1}</span>
                                            </div>
                                            <p className="text-sm text-foreground">{recommendation}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Client Modal */}
            {showAddClientModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto scrollbar-modal">
                        <div className="px-6 py-4 border-b border-border">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Add New Client</h3>
                                <button
                                    onClick={() => setShowAddClientModal(false)}
                                    className="text-muted-foreground hover:text-muted-foreground"
                                >
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleClientSubmit} className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={clientFormData.name}
                                        onChange={handleClientFormChange}
                                        required
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={clientFormData.email}
                                        onChange={handleClientFormChange}
                                        required
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        name="phoneE164"
                                        value={clientFormData.phoneE164}
                                        onChange={handleClientFormChange}
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                                    <select
                                        name="status"
                                        value={clientFormData.status}
                                        onChange={handleClientFormChange}
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        <option value="prospect">Prospect</option>
                                        <option value="active">Active</option>
                                        <option value="paused">Paused</option>
                                        <option value="closed_lost">Closed Lost</option>
                                        <option value="churned">Churned</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Country of Nationality</label>
                                    <select
                                        name="countryNationality"
                                        value={clientFormData.countryNationality}
                                        onChange={handleClientFormChange}
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        <option value="ES">Spain</option>
                                        <option value="FR">France</option>
                                        <option value="DE">Germany</option>
                                        <option value="IT">Italy</option>
                                        <option value="GB">United Kingdom</option>
                                        <option value="US">United States</option>
                                        <option value="CA">Canada</option>
                                        <option value="AU">Australia</option>
                                        <option value="NL">Netherlands</option>
                                        <option value="BE">Belgium</option>
                                        <option value="CH">Switzerland</option>
                                        <option value="AT">Austria</option>
                                        <option value="SE">Sweden</option>
                                        <option value="NO">Norway</option>
                                        <option value="DK">Denmark</option>
                                        <option value="FI">Finland</option>
                                        <option value="PT">Portugal</option>
                                        <option value="IE">Ireland</option>
                                        <option value="LU">Luxembourg</option>
                                        <option value="MT">Malta</option>
                                        <option value="CY">Cyprus</option>
                                        <option value="GR">Greece</option>
                                        <option value="PL">Poland</option>
                                        <option value="CZ">Czech Republic</option>
                                        <option value="HU">Hungary</option>
                                        <option value="SK">Slovakia</option>
                                        <option value="SI">Slovenia</option>
                                        <option value="HR">Croatia</option>
                                        <option value="RO">Romania</option>
                                        <option value="BG">Bulgaria</option>
                                        <option value="LT">Lithuania</option>
                                        <option value="LV">Latvia</option>
                                        <option value="EE">Estonia</option>
                                        <option value="XX">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Country of Residence</label>
                                    <select
                                        name="countryResidence"
                                        value={clientFormData.countryResidence}
                                        onChange={handleClientFormChange}
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        <option value="ES">Spain</option>
                                        <option value="FR">France</option>
                                        <option value="DE">Germany</option>
                                        <option value="IT">Italy</option>
                                        <option value="GB">United Kingdom</option>
                                        <option value="US">United States</option>
                                        <option value="CA">Canada</option>
                                        <option value="AU">Australia</option>
                                        <option value="NL">Netherlands</option>
                                        <option value="BE">Belgium</option>
                                        <option value="CH">Switzerland</option>
                                        <option value="AT">Austria</option>
                                        <option value="SE">Sweden</option>
                                        <option value="NO">Norway</option>
                                        <option value="DK">Denmark</option>
                                        <option value="FI">Finland</option>
                                        <option value="PT">Portugal</option>
                                        <option value="IE">Ireland</option>
                                        <option value="LU">Luxembourg</option>
                                        <option value="MT">Malta</option>
                                        <option value="CY">Cyprus</option>
                                        <option value="GR">Greece</option>
                                        <option value="PL">Poland</option>
                                        <option value="CZ">Czech Republic</option>
                                        <option value="HU">Hungary</option>
                                        <option value="SK">Slovakia</option>
                                        <option value="SI">Slovenia</option>
                                        <option value="HR">Croatia</option>
                                        <option value="RO">Romania</option>
                                        <option value="BG">Bulgaria</option>
                                        <option value="LT">Lithuania</option>
                                        <option value="LV">Latvia</option>
                                        <option value="EE">Estonia</option>
                                        <option value="XX">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Preferred Language</label>
                                    <select
                                        name="preferredLanguage"
                                        value={clientFormData.preferredLanguage}
                                        onChange={handleClientFormChange}
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        <option value="es">Spanish</option>
                                        <option value="en">English</option>
                                        <option value="fr">French</option>
                                        <option value="de">German</option>
                                        <option value="it">Italian</option>
                                        <option value="pt">Portuguese</option>
                                        <option value="nl">Dutch</option>
                                        <option value="sv">Swedish</option>
                                        <option value="no">Norwegian</option>
                                        <option value="da">Danish</option>
                                        <option value="fi">Finnish</option>
                                        <option value="pl">Polish</option>
                                        <option value="cs">Czech</option>
                                        <option value="hu">Hungarian</option>
                                        <option value="sk">Slovak</option>
                                        <option value="sl">Slovenian</option>
                                        <option value="hr">Croatian</option>
                                        <option value="ro">Romanian</option>
                                        <option value="bg">Bulgarian</option>
                                        <option value="lt">Lithuanian</option>
                                        <option value="lv">Latvian</option>
                                        <option value="et">Estonian</option>
                                        <option value="el">Greek</option>
                                        <option value="mt">Maltese</option>
                                        <option value="cy">Welsh</option>
                                        <option value="ga">Irish</option>
                                        <option value="eu">Basque</option>
                                        <option value="ca">Catalan</option>
                                        <option value="gl">Galician</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Time Zone</label>
                                    <select
                                        name="timezone"
                                        value={clientFormData.timezone}
                                        onChange={handleClientFormChange}
                                        className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    >
                                        <option value="Europe/Madrid">Europe/Madrid</option>
                                        <option value="Europe/London">Europe/London</option>
                                        <option value="Europe/Paris">Europe/Paris</option>
                                        <option value="Europe/Berlin">Europe/Berlin</option>
                                        <option value="America/New_York">America/New_York</option>
                                        <option value="America/Los_Angeles">America/Los_Angeles</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Lead Source</label>
                                <input
                                    type="text"
                                    name="leadSource"
                                    value={clientFormData.leadSource}
                                    onChange={handleClientFormChange}
                                    placeholder="e.g., Website, Referral, Social Media"
                                    className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                />
                            </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">Client Partner</label>
                                    <ComboboxSearch
                                        data={[
                                            { value: '', label: 'Select Client Partner' },
                                            ...adminUsers.map(admin => ({
                                                value: admin._id,
                                                label: `${admin.name} (${admin.email})`
                                            }))
                                        ]}
                                        value={clientFormData.clientPartnerUserId}
                                        onValueChange={(value) => setClientFormData(prev => ({
                                            ...prev,
                                            clientPartnerUserId: value
                                        }))}
                                        placeholder="Search and select client partner..."
                                        className="w-full"
                                    />
                                </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
                                <textarea
                                    name="notes"
                                    value={clientFormData.notes}
                                    onChange={handleClientFormChange}
                                    rows="3"
                                    className="w-full border border-input rounded-md px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-border">
                                <button
                                    type="button"
                                    onClick={() => setShowAddClientModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: branding.primaryColor }}
                                >
                                    Add Client
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Activity Modal */}
            {showActivityModal && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 overflow-y-auto h-full w-full z-[9999]">
                    <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-card max-h-[90vh] overflow-y-auto scrollbar-modal">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-foreground">Log Activity</h3>
                                <button
                                    onClick={() => setShowActivityModal(false)}
                                    className="text-muted-foreground hover:text-muted-foreground"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                try {
                                    const activityData = {
                                        type: activityForm.type,
                                        subject: activityForm.subject,
                                        description: activityForm.description,
                                        accountId: accountId,
                                        scheduledDate: activityForm.scheduledDate,
                                        duration: activityForm.duration,
                                        outcome: activityForm.outcome,
                                        nextSteps: activityForm.nextSteps,
                                        priority: 'medium',
                                        importance: 'medium',
                                        status: 'planned'
                                    };
                                    
                                    await activityAPI.create(activityData);
                                    toast.success('Activity logged successfully');
                                    setShowActivityModal(false);
                                    setActivityForm({
                                        type: 'call',
                                        subject: '',
                                        description: '',
                                        scheduledDate: '',
                                        duration: 30,
                                        outcome: '',
                                        nextSteps: ''
                                    });
                                    
                                    // Refresh activities list
                                    await fetchActivities();
                                } catch (error) {
                                    console.error('Error creating activity:', error);
                                    toast.error('Failed to log activity');
                                }
                            }}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Activity Type
                                        </label>
                                        <select
                                            value={activityForm.type}
                                            onChange={(e) => setActivityForm(prev => ({ ...prev, type: e.target.value }))}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="call">Phone Call</option>
                                            <option value="email">Email</option>
                                            <option value="meeting">Meeting</option>
                                            <option value="task">Task</option>
                                            <option value="note">Note</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Subject
                                        </label>
                                        <input
                                            type="text"
                                            value={activityForm.subject}
                                            onChange={(e) => setActivityForm(prev => ({ ...prev, subject: e.target.value }))}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Enter activity subject"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            value={activityForm.description}
                                            onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                                            rows={4}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Describe the activity..."
                                            required
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Duration (minutes)
                                            </label>
                                            <input
                                                type="number"
                                                value={activityForm.duration}
                                                onChange={(e) => setActivityForm(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                                                className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                min="1"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Outcome
                                            </label>
                                            <select
                                                value={activityForm.outcome}
                                                onChange={(e) => setActivityForm(prev => ({ ...prev, outcome: e.target.value }))}
                                                className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">Select outcome</option>
                                                <option value="positive">Positive</option>
                                                <option value="neutral">Neutral</option>
                                                <option value="negative">Negative</option>
                                                <option value="follow-up">Follow-up Required</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Next Steps
                                        </label>
                                        <textarea
                                            value={activityForm.nextSteps}
                                            onChange={(e) => setActivityForm(prev => ({ ...prev, nextSteps: e.target.value }))}
                                            rows={2}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="What are the next steps?"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-border mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowActivityModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: branding.primaryColor }}
                                    >
                                        Log Activity
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Revenue Modal */}
            {showRevenueModal && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 overflow-y-auto h-full w-full z-[9999]">
                    <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-card max-h-[90vh] overflow-y-auto scrollbar-modal">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-foreground">Add Revenue</h3>
                                <button
                                    onClick={() => setShowRevenueModal(false)}
                                    className="text-muted-foreground hover:text-muted-foreground"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                // Handle revenue submission
                                console.log('Revenue submitted:', revenue);
                                toast.success('Revenue added successfully');
                                setShowRevenueModal(false);
                            }}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Revenue Amount
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <CurrencyDollarIcon className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={revenue}
                                                onChange={(e) => setRevenue(parseFloat(e.target.value) || 0)}
                                                className="block w-full pl-10 pr-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Revenue Type
                                        </label>
                                        <select
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="one-time">One-time Payment</option>
                                            <option value="recurring">Recurring Revenue</option>
                                            <option value="commission">Commission</option>
                                            <option value="consulting">Consulting Fee</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            rows={3}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Describe the revenue source..."
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Date
                                            </label>
                                            <input
                                                type="date"
                                                defaultValue={new Date().toISOString().split('T')[0]}
                                                className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Status
                                            </label>
                                            <select
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="received">Received</option>
                                                <option value="pending">Pending</option>
                                                <option value="invoiced">Invoiced</option>
                                                <option value="overdue">Overdue</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-border mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowRevenueModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                                    >
                                        Add Revenue
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Opportunity Modal */}
            {showOpportunityModal && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 overflow-y-auto h-full w-full z-[9999]">
                    <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-card max-h-[90vh] overflow-y-auto scrollbar-modal">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-foreground">
                                    {editingOpportunity ? 'Edit Opportunity' : 'Add Opportunity'}
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowOpportunityModal(false);
                                        resetOpportunityForm();
                                    }}
                                    className="text-muted-foreground hover:text-muted-foreground"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            
                            <form onSubmit={editingOpportunity ? handleUpdateOpportunity : handleAddOpportunity}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Opportunity Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={opportunityForm.name}
                                            onChange={handleOpportunityFormChange}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="e.g., Family Relocation Package"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Value
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <CurrencyDollarIcon className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <input
                                                type="number"
                                                step="0.01"
                                                name="value"
                                                value={opportunityForm.value}
                                                onChange={handleOpportunityFormChange}
                                                className="block w-full pl-10 pr-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Stage
                                            </label>
                                            <select
                                                name="stage"
                                                value={opportunityForm.stage}
                                                onChange={handleOpportunityFormChange}
                                                className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="prospecting">Prospecting</option>
                                                <option value="qualification">Qualification</option>
                                                <option value="proposal">Proposal</option>
                                                <option value="negotiation">Negotiation</option>
                                                <option value="closed_won">Closed Won</option>
                                                <option value="closed_lost">Closed Lost</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Probability (%)
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                name="probability"
                                                value={opportunityForm.probability}
                                                onChange={handleOpportunityFormChange}
                                                className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Link to Client (Optional)
                                        </label>
                                        <ComboboxSearch
                                            data={[
                                                { value: '', label: 'No client linked' },
                                                ...clients.map(client => ({
                                                    value: client._id,
                                                    label: `${`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || 'Unknown Client'} (${client.email})`
                                                }))
                                            ]}
                                            value={opportunityForm.linkedClientId}
                                            onValueChange={(value) => {
                                                setOpportunityForm(prev => ({
                                                    ...prev,
                                                    linkedClientId: value
                                                }));
                                            }}
                                            placeholder="Search and select client..."
                                            className="w-full"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Close Date
                                        </label>
                                        <input
                                            type="date"
                                            name="closeDate"
                                            value={opportunityForm.closeDate}
                                            onChange={handleOpportunityFormChange}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            rows={3}
                                            name="description"
                                            value={opportunityForm.description}
                                            onChange={handleOpportunityFormChange}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Describe the opportunity..."
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-border mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowOpportunityModal(false);
                                            resetOpportunityForm();
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: branding.primaryColor }}
                                    >
                                        {editingOpportunity ? 'Update Opportunity' : 'Add Opportunity'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Modal */}
            {showEmailModal && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 overflow-y-auto h-full w-full z-[9999]">
                    <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-card max-h-[90vh] overflow-y-auto scrollbar-modal">
                        <div className="mt-3">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-foreground">
                                    Send Email to {account?.name}
                                </h3>
                                <button
                                    onClick={() => setShowEmailModal(false)}
                                    className="text-muted-foreground hover:text-muted-foreground"
                                >
                                    <span className="sr-only">Close</span>
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <form onSubmit={(e) => { e.preventDefault(); handleSendEmail(); }}>
                                <div className="space-y-4">
                                    {/* Template Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Email Template
                                        </label>
                                        <select
                                            value={emailForm.templateId}
                                            onChange={(e) => handleEmailFormChange('templateId', e.target.value)}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select a template</option>
                                            {emailTemplates.map(template => (
                                                <option key={template._id} value={template._id}>
                                                    {template.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Recipient Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Recipient Email
                                        </label>
                                        <input
                                            type="email"
                                            value={emailForm.recipientEmail}
                                            onChange={(e) => handleEmailFormChange('recipientEmail', e.target.value)}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        />
                                    </div>

                                    {/* Recipient Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Recipient Name
                                        </label>
                                        <input
                                            type="text"
                                            value={emailForm.recipientName}
                                            onChange={(e) => handleEmailFormChange('recipientName', e.target.value)}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        />
                                    </div>

                                    {/* Template Variables */}
                                    {selectedTemplate && selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                Template Variables
                                            </label>
                                            <div className="space-y-2">
                                                {selectedTemplate.variables.map((variable, index) => (
                                                    <div key={index}>
                                                        <label className="block text-xs text-muted-foreground mb-1">
                                                            {variable}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={emailForm.variables[variable] || ''}
                                                            onChange={(e) => {
                                                                const newVariables = { ...emailForm.variables };
                                                                newVariables[variable] = e.target.value;
                                                                setEmailForm(prev => ({ ...prev, variables: newVariables }));
                                                            }}
                                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                            placeholder={`Enter ${variable}`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Template Preview */}
                                    {selectedTemplate && (
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                Email Preview
                                            </label>
                                            <div className="border border-border rounded-lg overflow-hidden bg-card">
                                                <div className="bg-blue-50 px-4 py-3 border-b border-border">
                                                    <div className="flex items-center gap-2">
                                                        <EnvelopeIcon className="h-4 w-4 text-blue-600" />
                                                        <span className="text-sm font-medium text-blue-900">Email Preview</span>
                                                    </div>
                                                </div>
                                                
                                                <div className="px-4 py-3 bg-muted border-b border-border">
                                                    <div className="text-sm">
                                                        <span className="font-medium text-foreground">Subject:</span>
                                                        <span className="ml-2 text-foreground">
                                                            {replaceVariables(selectedTemplate.subject, emailForm.variables)}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                <div className="p-4">
                                                    <div className="prose prose-sm max-w-none">
                                                        <div 
                                                            dangerouslySetInnerHTML={{ 
                                                                __html: replaceVariables(selectedTemplate.bodyHtml, emailForm.variables)
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-border mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowEmailModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={sendingEmail || !emailForm.templateId}
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
                                        style={{ backgroundColor: branding.primaryColor }}
                                    >
                                        {sendingEmail ? 'Sending...' : 'Send Email'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Account Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 overflow-y-auto h-full w-full z-[9999]">
                    <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-card max-h-[90vh] overflow-y-auto scrollbar-modal">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-foreground">Edit Account</h3>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="text-muted-foreground hover:text-muted-foreground"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                try {
                                    const response = await accountAPI.update(accountId, {
                                        ...formData,
                                        logoUrl: formData.logoUrl
                                    });
                                    const responseData = response.data;
                                    if (responseData.success) {
                                        setShowEditModal(false);
                                        await fetchAccountDetails(); // Refresh the data
                                        toast.success('Account updated successfully');
                                    } else {
                                        toast.error('Failed to update account');
                                    }
                                } catch (error) {
                                    toast.error('Failed to update account');
                                }
                            }}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Company Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Company Size
                                        </label>
                                        <select
                                            name="size"
                                            value={formData.size}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select size</option>
                                            <option value="1-5">1-5 employees</option>
                                            <option value="5-10">5-10 employees</option>
                                            <option value="10-50">10-50 employees</option>
                                            <option value="50-100">50-100 employees</option>
                                            <option value="100-1000">100-1000 employees</option>
                                            <option value="1000+">1000+ employees</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Website
                                        </label>
                                        <input
                                            type="url"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="https://example.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Logo URL
                                        </label>
                                        <input
                                            type="url"
                                            name="logoUrl"
                                            value={formData.logoUrl}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="https://via.placeholder.com/64x64/3B82F6/FFFFFF?text=LOGO"
                                        />
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            Use a publicly accessible image URL. Test URLs: via.placeholder.com, picsum.photos, or upload to imgur.com
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Lifecycle Stage
                                        </label>
                                        <select
                                            name="lifecycle"
                                            value={formData.lifecycle}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="lead">Lead</option>
                                            <option value="prospect">Prospect</option>
                                            <option value="customer">Customer</option>
                                            <option value="churned">Churned</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Primary Contact Name
                                        </label>
                                        <input
                                            type="text"
                                            name="primaryContact.name"
                                            value={formData.primaryContact?.name || ''}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Primary Contact Email
                                        </label>
                                        <input
                                            type="email"
                                            name="primaryContact.email"
                                            value={formData.primaryContact?.email || ''}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Address Line 1
                                        </label>
                                        <input
                                            type="text"
                                            name="address.line1"
                                            value={formData.address?.line1 || ''}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            name="address.city"
                                            value={formData.address?.city || ''}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Country
                                        </label>
                                        <input
                                            type="text"
                                            name="address.country"
                                            value={formData.address?.country || ''}
                                            onChange={handleFormChange}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="ES"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-border mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowEditModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: branding.primaryColor }}
                                    >
                                        Update Account
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountDetailView;
